import { Box, Button, CircularProgress, PaletteMode, Stack, Typography } from '@mui/material'
import { useEffect } from 'react'
import { useGetCurrentUserQuery } from '../../store/redux/reducers/usersSlice'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { currentUserActions } from '../../store/redux/reducers/currentUserSlice'
import { useTranslation } from 'react-i18next'
import { setMode } from '../../store/redux/reducers/darkModeSlice'
import dayjs from 'dayjs'
import { setSettings } from '../../store/redux/reducers/settingsSlice'

type LoadingUserDataViewProps = {
  to?: string
}

const LoadingUserDataView = ({ to }: LoadingUserDataViewProps) => {
  const { data: currentUser, isLoading, isError, isSuccess } = useGetCurrentUserQuery({})
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const storedLanguage = localStorage.getItem('language')
  const initialLanguage = storedLanguage || 'pl'

  useEffect(() => {
    i18n.changeLanguage(initialLanguage).then()
    dayjs.locale(initialLanguage)

    let mode = localStorage.getItem('darkMode')
    if (mode !== null) {
      dispatch(setMode(mode as PaletteMode))
    } else {
      localStorage.setItem('darkMode', 'light')
    }
  }, [i18n, initialLanguage, dispatch])

  useEffect(() => {
    if (isError) {
      navigate('/login')
    }
  }, [isError, navigate])

  useEffect(() => {
    if (isSuccess && currentUser) {
      dispatch(currentUserActions.setCurrentUser({ currentUser: currentUser.user }))
      if (currentUser.settings) {
        dispatch(setSettings(currentUser.settings))
      }
      navigate(to || '/home', { replace: true })
    }
  }, [isSuccess, currentUser, dispatch, navigate, to])

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 5,
        bgcolor: 'background.main',
        color: 'text.base',
      }}
    >
      {isLoading && (
        <Stack direction="column" alignItems="center" gap={5}>
          <CircularProgress />
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}
          >
            <Typography variant="h4" sx={{ color: 'text.highEmphasis' }}>
              {t('page.loading.fetching')}
            </Typography>
            <Typography variant="body1">{t('page.loading.dataOnItsWay')}</Typography>
          </Box>
        </Stack>
      )}

      {isError && (
        <Stack direction="column" gap={5} alignItems="center">
          <Typography variant="h1" sx={{ color: 'text.mediumEmphasis' }}>
            {':('}
          </Typography>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}
          >
            <Typography variant="h4" sx={{ color: 'text.highEmphasis' }}>
              {t('page.loading.error')}
            </Typography>
            <Typography variant="body1">{t('page.loading.logout')}</Typography>
          </Box>
          <Box>
            <Button variant="contained" color="primary" onClick={() => navigate('/login')}>
              {t('page.loading.return')}
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  )
}

export default LoadingUserDataView